/**
 * Secure error handling utility that prevents information disclosure
 * while maintaining useful error information for developers
 */

import { secureLogger } from './secure-logger';

interface ErrorDetails {
  code?: string;
  message: string;
  details?: unknown;
  userMessage?: string;
}

interface SecureError {
  code: string;
  message: string;
  timestamp: string;
  id: string;
}

// Sensitive error patterns that should be hidden from users
const SENSITIVE_ERROR_PATTERNS = [
  /database/i,
  /sql/i,
  /connection/i,
  /timeout/i,
  /unauthorized/i,
  /forbidden/i,
  /access denied/i,
  /internal server error/i,
  /stack trace/i,
  /file path/i,
  /directory/i,
  /configuration/i,
  /environment/i,
  /secret/i,
  /token/i,
  /key/i,
  /password/i
];

// Production-safe error messages
const SAFE_ERROR_MESSAGES = {
  AUTHENTICATION_FAILED: 'Credenciais inválidas. Verifique seu email e senha.',
  NETWORK_ERROR: 'Erro de conexão. Verifique sua conexão com a internet.',
  VALIDATION_ERROR: 'Dados inválidos. Verifique os campos e tente novamente.',
  PERMISSION_DENIED: 'Você não tem permissão para realizar esta ação.',
  RESOURCE_NOT_FOUND: 'Recurso não encontrado.',
  RATE_LIMIT_EXCEEDED: 'Muitas tentativas. Tente novamente em alguns minutos.',
  SERVER_ERROR: 'Erro interno do servidor. Tente novamente mais tarde.',
  UNKNOWN_ERROR: 'Erro inesperado. Tente novamente mais tarde.'
};

/**
 * Error classification for different types of errors
 */
enum ErrorType {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  NETWORK = 'NETWORK',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Classifies errors based on their characteristics
 */
function classifyError(error: unknown): ErrorType {
  const errorString = String(error).toLowerCase();
  const errorObject = error as { name?: string; code?: string; status?: number };

  // Authentication errors
  if (errorString.includes('unauthorized') ||
      errorString.includes('authentication') ||
      errorString.includes('invalid credentials') ||
      errorObject.code === 'NotAuthorizedException' ||
      errorObject.status === 401) {
    return ErrorType.AUTHENTICATION;
  }

  // Authorization errors
  if (errorString.includes('forbidden') ||
      errorString.includes('access denied') ||
      errorObject.status === 403) {
    return ErrorType.AUTHORIZATION;
  }

  // Validation errors
  if (errorString.includes('validation') ||
      errorString.includes('invalid') ||
      errorString.includes('required') ||
      errorObject.status === 400) {
    return ErrorType.VALIDATION;
  }

  // Network errors
  if (errorString.includes('network') ||
      errorString.includes('connection') ||
      errorString.includes('timeout') ||
      errorString.includes('fetch')) {
    return ErrorType.NETWORK;
  }

  // Server errors
  if (errorObject.status && errorObject.status >= 500) {
    return ErrorType.SERVER;
  }

  // Client errors
  if (errorObject.status && errorObject.status >= 400 && errorObject.status < 500) {
    return ErrorType.CLIENT;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Generates a unique error ID for tracking
 */
function generateErrorId(): string {
  return `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Sanitizes error messages to prevent information disclosure
 */
function sanitizeErrorMessage(message: string): string {
  let sanitized = message;

  // Check for sensitive patterns
  for (const pattern of SENSITIVE_ERROR_PATTERNS) {
    if (pattern.test(sanitized)) {
      return SAFE_ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  }

  // Remove file paths
  sanitized = sanitized.replace(/\/[^\s]*/g, '[PATH]');

  // Remove URLs except for safe ones
  sanitized = sanitized.replace(/https?:\/\/[^\s]+/g, '[URL]');

  // Remove stack traces
  sanitized = sanitized.replace(/\s+at\s+.*/g, '');

  return sanitized;
}

/**
 * Creates a user-safe error message based on error type
 */
function createUserMessage(errorType: ErrorType, originalMessage?: string): string {
  switch (errorType) {
    case ErrorType.AUTHENTICATION:
      return SAFE_ERROR_MESSAGES.AUTHENTICATION_FAILED;
    case ErrorType.AUTHORIZATION:
      return SAFE_ERROR_MESSAGES.PERMISSION_DENIED;
    case ErrorType.VALIDATION:
      return SAFE_ERROR_MESSAGES.VALIDATION_ERROR;
    case ErrorType.NETWORK:
      return SAFE_ERROR_MESSAGES.NETWORK_ERROR;
    case ErrorType.SERVER:
      return SAFE_ERROR_MESSAGES.SERVER_ERROR;
    default:
      return SAFE_ERROR_MESSAGES.UNKNOWN_ERROR;
  }
}

/**
 * Main error handling class
 */
export class SecureErrorHandler {
  /**
   * Handles an error securely, logging full details for developers
   * while returning safe information for users
   */
  static handle(error: unknown, context?: string): SecureError {
    const errorId = generateErrorId();
    const errorType = classifyError(error);
    const timestamp = new Date().toISOString();

    // Extract error details safely
    const errorObject = error as {
      name?: string;
      message?: string;
      code?: string;
      stack?: string;
      status?: number;
    };

    const originalMessage = errorObject.message || String(error);
    const userMessage = createUserMessage(errorType, originalMessage);

    // Log full error details for developers (only in development)
    if (import.meta.env.DEV) {
      secureLogger.error(`[${errorId}] ${context || 'Unhandled error'}`, {
        type: errorType,
        originalMessage,
        code: errorObject.code,
        name: errorObject.name,
        status: errorObject.status,
        stack: errorObject.stack?.substring(0, 500) // Limit stack trace length
      });
    } else {
      // In production, log minimal safe information
      secureLogger.error(`[${errorId}] Error in ${context || 'application'}`, {
        type: errorType,
        timestamp
      });
    }

    // Return safe error for user display
    return {
      code: errorType,
      message: userMessage,
      timestamp,
      id: errorId
    };
  }

  /**
   * Handles API errors specifically
   */
  static handleApiError(error: unknown, endpoint?: string): SecureError {
    const context = endpoint ? `API call to ${endpoint}` : 'API call';
    return this.handle(error, context);
  }

  /**
   * Handles authentication errors specifically
   */
  static handleAuthError(error: unknown, operation?: string): SecureError {
    const context = operation ? `Authentication: ${operation}` : 'Authentication';
    return this.handle(error, context);
  }

  /**
   * Handles validation errors with custom user messages
   */
  static handleValidationError(errors: string[], field?: string): SecureError {
    const errorId = generateErrorId();
    const timestamp = new Date().toISOString();

    const userMessage = field ?
      `Erro de validação no campo ${field}` :
      'Dados inválidos. Verifique os campos e tente novamente.';

    secureLogger.warn(`[${errorId}] Validation error`, {
      field,
      errors: errors.slice(0, 5) // Limit number of validation errors logged
    });

    return {
      code: ErrorType.VALIDATION,
      message: userMessage,
      timestamp,
      id: errorId
    };
  }

  /**
   * Creates a safe error for display without logging
   */
  static createSafeError(message: string, code: string = ErrorType.UNKNOWN): SecureError {
    return {
      code,
      message: sanitizeErrorMessage(message),
      timestamp: new Date().toISOString(),
      id: generateErrorId()
    };
  }
}

/**
 * React Error Boundary compatible error handler
 */
export function handleErrorBoundary(error: Error, errorInfo: { componentStack: string }): void {
  const errorId = generateErrorId();

  secureLogger.error(`[${errorId}] React Error Boundary`, {
    name: error.name,
    message: error.message,
    componentStack: errorInfo.componentStack.substring(0, 500)
  });

  // In production, you might want to send this to an error tracking service
  if (import.meta.env.PROD) {
    // Example: sendToErrorTracking(errorId, error);
  }
}

/**
 * Global error handler for unhandled promise rejections
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const secureError = SecureErrorHandler.handle(event.reason, 'Unhandled promise rejection');
    secureLogger.error('Unhandled promise rejection:', secureError);

    // Prevent the error from being logged to console in production
    if (import.meta.env.PROD) {
      event.preventDefault();
    }
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    const secureError = SecureErrorHandler.handle(event.error, 'Global JavaScript error');
    secureLogger.error('Global error:', secureError);
  });
}

// Export utility functions
export { ErrorType, SAFE_ERROR_MESSAGES };